import { useState, useEffect, useCallback } from 'react';
import { Mail, Save, Send, Loader2, CheckCircle2, AlertTriangle, KeyRound, Info } from 'lucide-react';

import { emailSettingsApi } from '@/api/emailSettings.api';
import type { EmailSettingDto, EmailProvider } from '@/api/emailSettings.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';

const inputCls = cn(
  'w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2',
  'bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100',
  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent',
);

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{hint}</p>}
  </div>
);

export const EmailSettingsPage = () => {
  const { hasPermission } = usePermission();
  const canView   = hasPermission(Permissions.EMAIL_SETTINGS_VIEW);
  const canManage = hasPermission(Permissions.EMAIL_SETTINGS_MANAGE);

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast,   setToast]   = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const [provider,  setProvider]  = useState<EmailProvider>('Smtp');
  const [apiKey,    setApiKey]    = useState('');          // blank = leave stored key alone
  const [hasApiKey, setHasApiKey] = useState(false);
  const [fromEmail, setFromEmail] = useState('');
  const [fromName,  setFromName]  = useState('myLedger');
  const [replyTo,   setReplyTo]   = useState('');
  const [isActive,  setIsActive]  = useState(true);
  const [testTo,    setTestTo]    = useState('');

  const show = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 6000);
  };

  const apply = (d: EmailSettingDto) => {
    setProvider(d.provider);
    setHasApiKey(d.hasApiKey);
    setFromEmail(d.fromEmail ?? '');
    setFromName(d.fromName ?? 'myLedger');
    setReplyTo(d.replyTo ?? '');
    setIsActive(d.isActive);
    setApiKey('');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailSettingsApi.get();
      if (res.data.data) apply(res.data.data);
    } catch {
      show('error', 'Could not load email settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await emailSettingsApi.update({
        provider,
        resendApiKey: apiKey.trim() || null,
        fromEmail: fromEmail.trim(),
        fromName: fromName.trim(),
        replyTo: replyTo.trim() || null,
        isActive,
      });
      if (res.data.data) apply(res.data.data);
      show('success', 'Email settings saved.');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      show('error', msg || 'Could not save email settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testTo.trim()) { show('error', 'Enter an address to send the test to.'); return; }
    setTesting(true);
    try {
      const res = await emailSettingsApi.sendTest(testTo.trim());
      show('success', res.data.message || 'Test email sent.');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      show('error', msg || 'Test email failed.');
    } finally {
      setTesting(false);
    }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        You do not have permission to view email settings.
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
          <Mail size={20} className="text-indigo-600 dark:text-indigo-300" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Email Settings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            How myLedger sends mail — password reset codes, emailed documents and notifications.
          </p>
        </div>
      </div>

      {toast && (
        <div className={cn(
          'flex items-start gap-2 rounded-lg px-4 py-3 text-sm border',
          toast.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
        )}>
          {toast.type === 'success' ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertTriangle size={16} className="mt-0.5" />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Provider */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Delivery method</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(['Resend', 'Smtp'] as EmailProvider[]).map(p => (
            <button
              key={p}
              type="button"
              disabled={!canManage}
              onClick={() => setProvider(p)}
              className={cn(
                'text-left rounded-lg border px-4 py-3 transition-colors disabled:opacity-60',
                provider === p
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 ring-1 ring-brand-500'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50',
              )}
            >
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {p === 'Resend' ? 'Resend (API)' : 'SMTP server'}
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                {p === 'Resend'
                  ? 'Sends over HTTPS. Works where outbound SMTP ports are blocked.'
                  : 'Uses the Smtp settings in the server configuration file.'}
              </div>
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input type="checkbox" checked={isActive} disabled={!canManage}
            onChange={e => setIsActive(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600" />
          Email sending is enabled
        </label>
      </section>

      {/* Resend credentials */}
      {provider === 'Resend' && (
        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 p-5 space-y-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <KeyRound size={15} /> Resend API key
          </h2>

          <Field
            label="API key"
            hint={hasApiKey
              ? 'A key is stored. Leave this blank to keep it; type a new one to replace it.'
              : 'Create a key at resend.com → API Keys. It is encrypted before being stored and is never shown again.'}
          >
            <input
              type="password"
              value={apiKey}
              disabled={!canManage}
              onChange={e => setApiKey(e.target.value)}
              placeholder={hasApiKey ? '•••••••••••••••• (unchanged)' : 're_xxxxxxxxxxxxxxxxxxxx'}
              className={inputCls}
              autoComplete="new-password"
            />
          </Field>

          <div className="flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2 text-[11px] text-blue-800 dark:text-blue-300">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              The sender address below must be on a domain you have verified in Resend, otherwise
              Resend rejects the message.
            </span>
          </div>
        </section>
      )}

      {/* Sender */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sender</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="From address" hint="e.g. noreply@yourcompany.com">
            <input value={fromEmail} disabled={!canManage}
              onChange={e => setFromEmail(e.target.value)}
              placeholder="noreply@yourcompany.com" className={inputCls} />
          </Field>
          <Field label="From name">
            <input value={fromName} disabled={!canManage}
              onChange={e => setFromName(e.target.value)}
              placeholder="myLedger" className={inputCls} />
          </Field>
        </div>
        <Field label="Reply-to (optional)" hint="Where replies should go, if not the From address.">
          <input value={replyTo} disabled={!canManage}
            onChange={e => setReplyTo(e.target.value)}
            placeholder="finance@yourcompany.com" className={inputCls} />
        </Field>
      </section>

      {/* Actions */}
      {canManage && (
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save settings</>}
          </button>

          <div className="flex items-center gap-2 ml-auto">
            <input value={testTo} onChange={e => setTestTo(e.target.value)}
              placeholder="you@example.com"
              className={cn(inputCls, 'w-56')} />
            <button onClick={handleTest} disabled={testing}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 whitespace-nowrap">
              {testing ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send test</>}
            </button>
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-400 dark:text-gray-500">
        Save first, then send a test — the test uses the settings as stored, not what is on screen.
      </p>
    </div>
  );
};

export default EmailSettingsPage;
